import React, { useState, useEffect, useRef } from "react";
import {
    Box, Typography, Avatar, List, ListItem, ListItemAvatar,
    ListItemText, Divider, InputBase, IconButton, styled, Paper, Badge
} from "@mui/material";
import {
    Search as SearchIcon, Chat as ChatIcon, EmojiEmotions as EmojiIcon,
    AttachFile as AttachIcon, Send as SendIcon, DoneAll as DoneAllIcon,
    Security as SecurityIcon, SupportAgent as SupportAgentIcon
} from "@mui/icons-material";

// Firebase Imports
import { db, auth } from "../firebase";
import {
    collection, addDoc, query, where, orderBy,
    onSnapshot, serverTimestamp, doc, writeBatch, limit
} from "firebase/firestore";

// --- STYLED COMPONENTS ---
const SidebarContainer = styled(Box)({
    width: 400,
    backgroundColor: "#fff",
    display: "flex",
    flexDirection: "column",
    borderRight: "1px solid #d1d7db",
    height: "100%",
});

const ChatWindow = styled(Box)({
    flex: 1,
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#efeae2",
    backgroundImage: "url('https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png')",
    height: "100%",
});

const ScrollableArea = styled(Box)({
    flex: 1,
    overflowY: "auto",
    '&::-webkit-scrollbar': { width: '6px' },
    '&::-webkit-scrollbar-thumb': { backgroundColor: '#ced0d1' },
});

const MessageBubble = styled(Paper)(({ isMine }) => ({
    padding: "6px 10px 4px 12px",
    maxWidth: "65%",
    marginBottom: 4,
    borderRadius: 8,
    alignSelf: isMine ? "flex-end" : "flex-start",
    backgroundColor: isMine ? "#dcf8c6" : "#ffffff",
    boxShadow: "0 1px 0.5px rgba(0,0,0,0.13)",
    position: "relative",
    borderTopRightRadius: isMine ? 0 : 8,
    borderTopLeftRadius: isMine ? 8 : 0,
}));

const ParentListItem = ({ parent, isSelected, onSelect, currentUser }) => {
    const [meta, setMeta] = useState({ lastMsg: "", lastTime: "", unreadCount: 0 });

    useEffect(() => {
        if (!currentUser || !parent.id) return;

        // CRITICAL: Must match the Mobile App ID logic
        const chatId = currentUser < parent.id ? `${currentUser}_${parent.id}` : `${parent.id}_${currentUser}`;
        
        // Listen to the messages subcollection for this specific chat
        const q = query(
            collection(db, "chats", chatId, "messages"), 
            orderBy("createdAt", "desc"),
            limit(20) // Get enough to count unread messages
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            if (!snapshot.empty) {
                const latestDoc = snapshot.docs[0].data();
                const time = latestDoc.createdAt?.toDate();
                
                setMeta({
                    lastMsg: latestDoc.text || "Media message",
                    lastTime: time ? time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "",
                    unreadCount: snapshot.docs.filter(doc => {
                        const d = doc.data();
                        return d.senderId !== currentUser && d.status !== "read";
                    }).length
                });
            } else {
                setMeta({ lastMsg: "No messages yet", lastTime: "", unreadCount: 0 });
            }
        });
        return unsubscribe;
    }, [parent.id, currentUser]);

    return (
        <React.Fragment>
            <ListItem
                button
                onClick={() => onSelect(parent)}
                selected={isSelected}
                sx={{
                    py: 1, // Compact padding
                    px: 2,
                    "&.Mui-selected": { bgcolor: "#f0f2f5" },
                    "&:hover": { bgcolor: "#f5f6f6" }
                }}
            >
                <ListItemAvatar sx={{ minWidth: 50 }}>
                    <Avatar sx={{ bgcolor: "#00a884", width: 40, height: 40, fontSize: 15 }}>
                        {parent.name?.charAt(0)}
                    </Avatar>
                </ListItemAvatar>
                <ListItemText
                    primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ fontWeight: meta.unreadCount > 0 ? 700 : 600, fontSize: '0.85rem' }}>
                                {parent.name}
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: '0.65rem', color: meta.unreadCount > 0 ? "#25D366" : "#667781" }}>
                                {meta.lastTime}
                            </Typography>
                        </Box>
                    }
                    secondary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.2 }}>
                            <Typography variant="caption" noWrap sx={{ 
                                maxWidth: '85%', 
                                fontSize: '0.75rem',
                                color: meta.unreadCount > 0 ? "#111b21" : "#667781" 
                            }}>
                                {meta.lastMsg}
                            </Typography>
                            {meta.unreadCount > 0 && (
                                <Box sx={{ 
                                    bgcolor: '#25D366', color: '#fff', borderRadius: '50%', 
                                    width: 16, height: 16, display: 'flex', 
                                    alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem' 
                                }}>
                                    {meta.unreadCount}
                                </Box>
                            )}
                        </Box>
                    }
                />
            </ListItem>
            <Divider variant="inset" component="li" sx={{ borderColor: "#f2f2f2", ml: "65px" }} />
        </React.Fragment>
    );
};

export default function SupportDashboard() {
    const [parents, setParents] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedParent, setSelectedParent] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const chatEndRef = useRef(null);
    const currentUser = auth.currentUser?.uid;

    // 1. Fetch Parents List
    useEffect(() => {
        const q = query(collection(db, "users"), where("role", "==", "Parent"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setParents(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        return unsubscribe;
    }, []);

    // 2. Real-time Message Listener & Auto-Read
    useEffect(() => {
        if (!selectedParent || !currentUser) return;

        const chatId = currentUser < selectedParent.id ? `${currentUser}_${selectedParent.id}` : `${selectedParent.id}_${currentUser}`;
        const q = query(collection(db, "chats", chatId, "messages"), orderBy("createdAt", "asc"));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            setMessages(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            
            const batch = writeBatch(db);
            let hasUpdates = false;
            snapshot.docs.forEach((msgDoc) => {
                const data = msgDoc.data();
                if (data.senderId !== currentUser && data.status !== "read") {
                    batch.update(doc(db, "chats", chatId, "messages", msgDoc.id), { status: "read", readAt: serverTimestamp() });
                    hasUpdates = true;
                }
            });
            if (hasUpdates) batch.commit();
        });
        return unsubscribe;
    }, [selectedParent?.id, currentUser]);

    useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

    const handleSend = async () => {
        if (!input.trim() || !selectedParent) return;
        const chatId = currentUser < selectedParent.id ? `${currentUser}_${selectedParent.id}` : `${selectedParent.id}_${currentUser}`;
        const text = input;
        setInput("");
        try {
            await addDoc(collection(db, "chats", chatId, "messages"), {
                text, senderId: currentUser, receiverId: selectedParent.id,
                createdAt: serverTimestamp(), status: "sent"
            });
        } catch (e) { console.error(e); }
    };

    const filteredParents = parents.filter(p => p.name?.toLowerCase().includes(searchQuery.toLowerCase()));

    return (
        <Box sx={{ display: "flex", height: "85vh", bgcolor: "#f0f2f5" }}>
            <Paper elevation={4} sx={{ display: "flex", flex: 1, overflow: "hidden", borderRadius: 2 }}>
                
                {/* SIDEBAR */}
                <SidebarContainer>
                    <Box sx={{ p: 2, bgcolor: "#fff" }}>
                        <Typography variant="h6" sx={{ fontWeight: 800, mb: 2, color: "#008069" }}>Support Desk</Typography>
                        <Box sx={{ display: "flex", alignItems: "center", bgcolor: "#f0f2f5", borderRadius: 2, px: 2, height: 40 }}>
                            <SearchIcon sx={{ color: "gray", fontSize: 20 }} />
                            <InputBase 
                                placeholder="Search parents..." 
                                sx={{ ml: 1, flex: 1, fontSize: 14 }} 
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </Box>
                    </Box>
                    <ScrollableArea>
                        <List sx={{ p: 0 }}>
                            {filteredParents.map(parent => (
                                <ParentListItem 
                                    key={parent.id} 
                                    parent={parent} 
                                    currentUser={currentUser}
                                    isSelected={selectedParent?.id === parent.id}
                                    onSelect={setSelectedParent}
                                />
                            ))}
                            
                        </List>
                    </ScrollableArea>
                </SidebarContainer>

                {/* CHAT WINDOW */}
                <ChatWindow>
                    {selectedParent ? (
                        <>
                            <Box sx={{ p: "10px 16px", bgcolor: "#f0f2f5", display: "flex", alignItems: "center", gap: 2, borderBottom: "1px solid #e0e0e0" }}>
                                <Avatar sx={{ bgcolor: "#00a884" }}>{selectedParent.name?.charAt(0)}</Avatar>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{selectedParent.name}</Typography>
                                    <Typography variant="caption" color="black">● Parent</Typography>
                                </Box>
                            </Box>
                            <ScrollableArea sx={{ p: 3, display: "flex", flexDirection: "column" }}>
                                {messages.map((msg) => (
                                    <MessageBubble key={msg.id} isMine={msg.senderId === currentUser}>
                                        <Typography sx={{ fontSize: 14.5 }}>{msg.text}</Typography>
                                        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mt: 0.2 }}>
                                            <Typography sx={{ fontSize: 10, color: "#667781" }}>
                                                {msg.createdAt?.toDate()?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) || "..."}
                                            </Typography>
                                            {msg.senderId === currentUser && (
                                                <DoneAllIcon sx={{ fontSize: 16, ml: 0.5, color: msg.status === "read" ? "#53bdeb" : "#8696a0" }} />
                                            )}
                                        </Box>
                                    </MessageBubble>
                                ))}
                                <div ref={chatEndRef} />
                            </ScrollableArea>
                            <Box sx={{ p: 2, bgcolor: "#f0f2f5", display: "flex", alignItems: "center", gap: 1.5 }}>
                                {/* <IconButton size="small"><EmojiIcon /></IconButton> */}
                                <InputBase
                                    fullWidth
                                    placeholder="Write your response..."
                                    sx={{ bgcolor: "#fff", borderRadius: 3, px: 2, py: 1, fontSize: 14, border: "1px solid #e0e0e0" }}
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                                />
                                <IconButton onClick={handleSend} sx={{ bgcolor: "#00a884", color: "#fff", "&:hover": { bgcolor: "#008069" } }}>
                                    <SendIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        </>
                    ) : (
                        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)" }}>
                            <Box sx={{ textAlign: 'center', p: 5 }}>
                                <Box sx={{ position: 'relative', display: 'inline-block', mb: 3 }}>
                                    <Box sx={{ width: 140, height: 140, borderRadius: '50%', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 10px 30px rgba(0,0,0,0.05)' }}>
                                        <SupportAgentIcon sx={{ fontSize: 70, color: '#00a884' }} />
                                    </Box>
                                    <Box sx={{ position: 'absolute', bottom: 10, right: 0, bgcolor: '#25D366', p: 1, borderRadius: '50%', border: '4px solid #f8f9fa' }}>
                                        <SecurityIcon sx={{ color: '#fff', fontSize: 18 }} />
                                    </Box>
                                </Box>
                                <Typography variant="h4" sx={{ fontWeight: 800, color: '#111b21', mb: 1 }}>Welcome to Support Desk</Typography>
                                <Typography variant="body1" sx={{ color: '#667781', maxWidth: 350, mx: 'auto', lineHeight: 1.6 }}>
                                    Provide real-time assistance to parents. Select a chat from the sidebar to begin helping.
                                </Typography>
                                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 2, color: '#8696a0' }}>
                                    <Typography variant="caption">✓ Real-time Sync</Typography>
                                    <Typography variant="caption">✓ End-to-End Encrypted</Typography>
                                </Box>
                            </Box>
                        </Box>
                    )}
                </ChatWindow>
            </Paper>
        </Box>
    );
}